import { useNavigate } from "react-router-dom";
import {
  IconBell,
  IconBellOff,
  IconDotsVertical,
  IconPencil,
  IconSettings,
} from "@tabler/icons-react";
import moment from "moment/moment";
import { Button as BaseButton } from "@base-ui/react/button";
import styles from "./BankCard.module.css";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
// @ts-expect-error pure js
import { BankProvider } from "../../utils/constants/linxo";
// @ts-expect-error pure js
import { BankAccountStatus } from "../../utils/constants/banks";
// @ts-expect-error pure js
import useAuth from "../../hooks/use-auth.js";
import Text from "../UI/Text";
import Status from "../UI/Status";
import Tooltip from "../UI/Tooltip";
import DropdownMenu from "../UI/DropdownMenu";
import Button from "../UI/Button";
import Avatar from "../UI/Avatar";
import type { Bank } from "../../models/bank";
import type { ProfileBailleur } from "../Profile/types";
import IconButton from "../UI/IconButton.tsx";

const ROOT_PATH = "/bailleur/finances/";

interface BankCardProps {
  bank: Bank;
  onLinxoReconnect: (
    connectionId: string | null,
    channelDefinitionId: string | null,
  ) => void;
  onManageAccounts: (connectionId: string, channelDefinitionId: string) => void;
  onShowTransactions: (bank: Bank) => void;
}

export default function BankCard({
  bank,
  onLinxoReconnect,
  onManageAccounts,
  onShowTransactions,
}: BankCardProps) {
  const { auth } = useAuth();
  const { profileBailleurs } = useProfileBailleurs();
  const nav = useNavigate();

  const reconnectionHandler = () => {
    if (bank.provider === BankProvider.LINXO) {
      onLinxoReconnect(
        bank.connection_id ?? null,
        bank.channel_definition_id ?? null,
      );
    } else {
      onLinxoReconnect(null, null);
    }
  };

  const manageAccountsHandler = () => {
    if (bank.connection_id && bank.channel_definition_id) {
      onManageAccounts(bank.connection_id, bank.channel_definition_id);
    }
  };

  const profileBailleur = profileBailleurs.find(
    (profile: ProfileBailleur) => profile.id === bank.id_profile_nom,
  );

  const bankLabel = bank.bank_metadata?.name || bank.nom || "Compte manuel";

  const isActive = bank.status === BankAccountStatus.ACTIVE;
  const isInactive = bank.status === BankAccountStatus.INACTIVE;
  const isUnconsented = bank.status === BankAccountStatus.UNCONSENTED;

  const canReconnect =
    bank.provider !== BankProvider.NORDIGEN || bank.id_compte === auth.userId;

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <BaseButton
      nativeButton={false}
      className={styles.card}
      aria-label={`Voir les transactions — ${bankLabel}`}
      onClick={() => onShowTransactions(bank)}
      render={<div />}
    >
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.bankInfo}>
            {bank.bank_metadata ? (
              <>
                <img
                  className={styles.logo}
                  src={bank.bank_metadata.logo}
                  alt={bank.bank_metadata.name}
                />
                <div className={styles.bankNames}>
                  <Text as="span" weight="medium">
                    {bank.bank_metadata.name}
                  </Text>
                  {bank.nom && (
                    <Text as="span" variant="deci" color="muted">
                      {bank.nom}
                    </Text>
                  )}
                  {bank.compte_gestion && (
                    <Status color="info" text="Compte de gestion" as="span" />
                  )}
                </div>
              </>
            ) : (
              <>
                <Avatar initials={getInitials(bank.nom || "?")} size="sm" />
                <div className={styles.bankNames}>
                  <Text as="span" weight="medium">
                    {bankLabel}
                  </Text>
                </div>
              </>
            )}
          </div>
          <div
            className={styles.headerActions}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip
              content={
                bank.alert ? (
                  <>
                    Une alerte sera envoyée par e-mail si le solde du compte
                    descend en dessous de {currencyFormatter(bank.alert)}
                  </>
                ) : (
                  <>
                    Aucune alerte configurée. Il est possible d'envoyer une
                    alerte par e-mail lorsque le solde du compte descend en
                    dessous d'un certain montant.
                  </>
                )
              }
            >
              <IconButton
                icon={bank.alert ? IconBell : IconBellOff}
                label="Alerte"
              />
            </Tooltip>
            <DropdownMenu icon={IconDotsVertical} label="Actions">
              <DropdownMenu.Item
                icon={IconPencil}
                label="Éditer"
                onClick={() => nav(`${ROOT_PATH}edit/${bank.id}`)}
                requiresWrite
                profileId={bank.id_profile_nom ?? null}
              />
              {bank.provider === BankProvider.LINXO && !isInactive && (
                <DropdownMenu.Item
                  icon={IconSettings}
                  label="Gérer les comptes connectés"
                  onClick={manageAccountsHandler}
                  requiresWrite
                  profileId={bank.id_profile_nom ?? null}
                />
              )}
            </DropdownMenu>
          </div>
        </div>

        <div className={styles.content}>
          <div>
            <Text as="div" variant="deci">
              Solde
            </Text>
            <Text as="div" variant="kilo" weight="bold">
              {currencyFormatter(bank.montant)}
            </Text>
          </div>
          <div className={styles.statusSection}>
            {isActive && (
              <Tooltip
                content={
                  bank.date_maj
                    ? `Dernière synchronisation le ${moment
                        .utc(bank.date_maj)
                        .local()
                        .format("DD/MM/YYYY à HH:mm")}`
                    : "Jamais synchronisé"
                }
              >
                <Status color="success" text="Session active" />
              </Tooltip>
            )}
            {isInactive && (
              <div className={styles.statusWithAction}>
                <Status color="warning" text="Session expirée" />
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    reconnectionHandler();
                  }}
                  variant="tertiary"
                  disabled={!canReconnect}
                  requiresWrite
                  profileId={bank.id_profile_nom ?? null}
                >
                  {canReconnect ? "Se reconnecter" : "Voir compte propriétaire"}
                </Button>
              </div>
            )}
            {isUnconsented && (
              <div className={styles.statusWithAction}>
                <Status color="warning" text="Non synchronisé" />
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    manageAccountsHandler();
                  }}
                  variant="tertiary"
                  requiresWrite
                  profileId={bank.id_profile_nom ?? null}
                >
                  Gérer
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {profileBailleur && (
        <div className={styles.footer}>
          <Avatar
            initials={getInitials(profileBailleur.nom_profile)}
            size="sm"
          />
          <div>
            <Text as="div" variant="deci">
              Profil du bailleur
            </Text>
            <Text as="div" weight="medium">
              {profileBailleur.nom_profile}
            </Text>
          </div>
        </div>
      )}
    </BaseButton>
  );
}
